<template>
  <div class="firmware-r6 px">
    <button
      class="back"
      type="button"
      :aria-label="$t('back')"
      :title="$t('back')"
      @click="$router.go(-1)"
    >
      <i class="iconfont iconclose" aria-hidden="true" />
    </button>
    <img
      class="logo"
      :src="images.logo_white"
      alt="Angry Miao"
      @click="handleLogoClick"
    />
    <div class="version">
      <span>{{ $t("loader.firmwareVersion") }}</span>
      <span class="code">
        {{ version || "-" }}
        <i class="iconfont iconarrow-right" aria-hidden="true" />
      </span>
    </div>
    <div class="description" aria-hidden="true"></div>
    <button
      v-if="!isUnsupportedFirmwareProduct"
      class="button"
      type="button"
      :disabled="
        updating || checkingFirmware || (firmwareNotNewer && !canResumeUpgrade)
      "
      @click="openUpgradeWizard"
    >
      {{ firmwareButtonText }}
    </button>
    <CyberboardR6FirmwareProgress
      v-if="progressVisible"
      :text="statusText || $t('loader.updatingFirmware')"
      :error="upgradeError"
      :percent="progress"
      @close="closeProgress"
    />
    <div
      v-if="statusText && !progressStarted"
      class="firmware-status"
      role="status"
    >
      {{ statusText }}
    </div>
    <div v-else-if="isUnsupportedFirmwareProduct" class="firmware-status">
      {{ $t("cyberboardR6.firmwareUnsupportedDevice") }}
    </div>
    <div v-else-if="checkingFirmware" class="firmware-status">
      {{ $t("cyberboardR6.firmwareChecking") }}
    </div>
    <div v-else-if="!productId" class="firmware-status">
      {{ $t("cyberboardR6.firmwareCheckDeviceHint") }}
    </div>
    <div v-else-if="onlineFirmwareMeta && !canUpdateFirmware" class="tip">
      {{ $t("loader.isLatestVersion") }}
    </div>
    <div v-else class="tip">{{ $t("loader.isLatestVersion") }}</div>
    <a-modal
      :visible="upgradeModalVisible"
      :title="$t('cyberboardR6.firmwareWizardTitle')"
      :footer="null"
      :centered="true"
      :closable="!updating && !checkingFirmware"
      :maskClosable="false"
      :keyboard="false"
      wrapClassName="r6-firmware-modal px"
      @cancel="closeUpgradeWizard"
    >
      <ol class="wizard-steps">
        <li
          v-for="step in upgradeSteps"
          :key="step.key"
          class="wizard-step"
          :class="step.state"
        >
          <span class="wizard-step-marker" aria-hidden="true">
            {{ step.state === "done" ? "✓" : step.number }}
          </span>
          <strong>{{ step.title }}</strong>
        </li>
      </ol>

      <div class="wizard-panel">
        <template v-if="upgradeStep === 0 && !localFirmwareMode">
          <strong>{{ $t("cyberboardR6.firmwareStepEnter") }}</strong>
          <span>{{ $t("cyberboardR6.firmwareStepEnterHint") }}</span>
        </template>
        <template v-else-if="upgradeStep === 1">
          <template v-if="localFirmwareMode">
            <strong>{{ $t("cyberboardR6.firmwareStepEnter") }}</strong>
            <span>{{ $t("cyberboardR6.firmwareStepEnterHint") }}</span>
          </template>
          <template v-else>
            <strong>{{ $t("cyberboardR6.firmwareStepDfu") }}</strong>
            <span>{{ $t("cyberboardR6.firmwareStepDfuHint") }}</span>
          </template>
        </template>
        <template v-else-if="localFirmwareMode && upgradeStep === 0">
          <strong>{{ $t("cyberboardR6.firmwareStepFile") }}</strong>
          <span>{{ $t("cyberboardR6.firmwareStepFileHint") }}</span>
          <label
            class="firmware-file-picker"
            :class="{
              disabled: checkingFirmware,
              selected: selectedFirmwareFile,
            }"
          >
            <input
              type="file"
              accept=".zip,application/zip"
              :disabled="checkingFirmware"
              @change="selectLocalFirmware"
            />
            <span>{{
              selectedFirmwareFile
                ? selectedFirmwareFile.name
                : $t("cyberboardR6.firmwareChooseFile")
            }}</span>
          </label>
        </template>
        <template v-else-if="localFirmwareMode && upgradeStep === 2">
          <strong>{{ $t("cyberboardR6.firmwareStepDfu") }}</strong>
          <span>{{ $t("cyberboardR6.firmwareStepDfuHint") }}</span>
        </template>
        <template v-else-if="reconnectPending">
          <strong>{{ $t("cyberboardR6.firmwareWaitReconnect") }}</strong>
          <span>{{ $t("cyberboardR6.firmwareStepReconnectHint") }}</span>
        </template>
        <template v-else>
          <div class="firmware-version-row">
            <span>{{ $t("cyberboardR6.firmwareCurrentVersion") }}</span>
            <code>{{ version || "-" }}</code>
          </div>
          <span class="firmware-version-arrow" aria-hidden="true">→</span>
          <div class="firmware-version-row target">
            <span>{{ $t("cyberboardR6.firmwareTargetVersion") }}</span>
            <code>{{ onlineFirmwareMeta && onlineFirmwareMeta.version }}</code>
          </div>
          <p
            v-if="onlineFirmwareMeta && onlineFirmwareMeta.changelog"
            class="wizard-changelog"
          >
            <strong>{{ $t("cyberboardR6.firmwareChangelog") }}</strong>
            <span>{{ onlineFirmwareMeta.changelog }}</span>
          </p>
        </template>
      </div>

      <div v-if="upgradeError" class="wizard-error" role="alert">
        {{ upgradeError }}
      </div>
      <div class="wizard-actions">
        <button
          class="wizard-next"
          type="button"
          :disabled="stepActionDisabled"
          @click="advanceUpgradeStep"
        >
          {{ stepActionText }}
        </button>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { mapState } from "vuex";
import CyberboardR6FirmwareProgress from "./CyberboardR6FirmwareProgress.vue";
import { getCyberboardR6Bridge } from "./CyberboardR6CdcPanel.vue";
import {
  requestCyberboardR6DfuPort,
  updateCyberboardR6Firmware,
  validateCyberboardR6FirmwarePackage,
  waitForCyberboardR6Reconnect,
} from "./utils/cyberboardR6SecureDfu";
import {
  compareR6FirmwareVersions,
  isR6FirmwareProductAllowed,
  normalizeR6FirmwareResponse,
} from "./utils/cyberboardR6Firmware";

const FIRMWARE_CHECK_API_URL = "https://diy.angrymiao.com/api/firmware/check";
const FIRMWARE_CHECK_KEY = "CBR6";
const FIRMWARE_CHECK_PASSWORD = "qaz028";

export default {
  name: "FirmwareR6",
  components: { CyberboardR6FirmwareProgress },
  data() {
    return {
      version: "",
      productId: "",
      onlineFirmwareMeta: null,
      dfuPort: null,
      upgradeModalVisible: false,
      upgradeStep: 0,
      localFirmwareMode: false,
      selectedFirmwareFile: null,
      reconnectPending: false,
      logoClickCount: 0,
      logoClickTimer: null,
      upgradeError: "",
      updating: false,
      progress: 0,
      statusText: "",
      progressStarted: false,
      checkingFirmware: false,
    };
  },
  computed: {
    ...mapState("assets", { images: "images" }),
    isUnsupportedFirmwareProduct() {
      return Boolean(
        this.productId && !isR6FirmwareProductAllowed(this.productId)
      );
    },
    canUpdateFirmware() {
      return (
        isR6FirmwareProductAllowed(this.productId) &&
        this.onlineFirmwareMeta &&
        compareR6FirmwareVersions(
          this.version,
          this.onlineFirmwareMeta.version
        ) === -1
      );
    },
    firmwareButtonText() {
      if (
        this.canResumeUpgrade ||
        (!this.localFirmwareMode && this.upgradeStep === 1)
      )
        return this.$t("cyberboardR6.firmwareResumeUpdate");
      if (this.checkingFirmware)
        return this.$t("cyberboardR6.firmwareChecking");
      if (!this.productId) return this.$t("cyberboardR6.firmwareCheckDevice");
      return this.$t("cyberboardR6.firmwareUpdate");
    },
    firmwareNotNewer() {
      return Boolean(this.onlineFirmwareMeta && !this.canUpdateFirmware);
    },
    progressVisible() {
      return this.progressStarted;
    },
    canResumeUpgrade() {
      if (this.localFirmwareMode) {
        return Boolean(
          this.selectedFirmwareFile &&
            this.upgradeStep > 0 &&
            this.upgradeStep <= 3
        );
      }
      return Boolean(this.upgradeStep === 2 && this.dfuPort);
    },
    upgradeSteps() {
      const definitions = this.localFirmwareMode
        ? [
            ["file", "firmwareStepFile", "firmwareStepFileHint"],
            ["enter", "firmwareStepEnter", "firmwareStepEnterHint"],
            ["dfu", "firmwareStepDfu", "firmwareStepDfuHint"],
            ["transfer", "firmwareStartUpdate", "firmwareStepTransferHint"],
          ]
        : [
            ["enter", "firmwareStepEnter", "firmwareStepEnterHint"],
            ["dfu", "firmwareStepDfu", "firmwareStepDfuHint"],
            ["package", "firmwareStepPackage", "firmwareStepPackageHint"],
            ["transfer", "firmwareStartUpdate", "firmwareStepTransferHint"],
          ];
      return definitions.map(([key, title, description], index) => ({
        key,
        number: index + 1,
        title: this.$t(`cyberboardR6.${title}`),
        description: this.$t(`cyberboardR6.${description}`),
        state: this.stepState(index),
      }));
    },
    stepActionText() {
      if (this.reconnectPending)
        return this.$t("cyberboardR6.firmwareWaitReconnect");
      const keys = this.localFirmwareMode
        ? [
            "firmwareContinue",
            "firmwareEnterUpgrade",
            "firmwareSelectDfuPort",
            "firmwareStartUpdate",
          ]
        : [
            "firmwareEnterUpgrade",
            "firmwareSelectDfuPort",
            "firmwareStartUpdate",
          ];
      return this.$t(`cyberboardR6.${keys[this.upgradeStep] || keys[2]}`);
    },
    stepActionDisabled() {
      if (this.updating) return true;
      if (this.checkingFirmware) return true;
      if (this.localFirmwareMode) {
        if (this.upgradeStep === 0) return !this.selectedFirmwareFile;
        return (
          this.upgradeStep > 3 || (this.upgradeStep === 3 && !this.dfuPort)
        );
      }
      if (this.firmwareNotNewer) return true;
      if (this.upgradeStep === 0) return false;
      if (this.upgradeStep === 1) return false;
      return this.upgradeStep > 2 || !this.dfuPort || !this.canUpdateFirmware;
    },
  },
  mounted() {
    const bridge = getCyberboardR6Bridge();
    if (bridge.isConnected && bridge.deviceInfo) {
      this.setDeviceInfo(bridge.deviceInfo);
      if (isR6FirmwareProductAllowed(this.productId)) {
        this.checkOnlineFirmware().catch((error) =>
          this.setUpgradeError(error)
        );
      }
    }
  },
  beforeDestroy() {
    clearTimeout(this.logoClickTimer);
  },
  methods: {
    handleLogoClick() {
      this.logoClickCount += 1;
      clearTimeout(this.logoClickTimer);
      if (this.logoClickCount === 5) {
        this.logoClickCount = 0;
        if (this.upgradeStep > 0) {
          this.openUpgradeWizard();
          return;
        }
        this.openLocalFirmwareWizard();
        return;
      }
      this.logoClickTimer = setTimeout(() => {
        this.logoClickCount = 0;
      }, 1200);
    },
    openLocalFirmwareWizard() {
      this.localFirmwareMode = true;
      this.selectedFirmwareFile = null;
      this.reconnectPending = false;
      this.upgradeModalVisible = true;
      this.upgradeError = "";
      this.upgradeStep = 0;
      this.dfuPort = null;
      this.progress = 0;
      this.progressStarted = false;
      this.statusText = "";
    },
    async selectLocalFirmware(event) {
      const input = event.target;
      const file = input.files && input.files[0];
      input.value = "";
      if (!file) return;
      if (!/\.zip$/i.test(file.name) || !file.size) {
        this.selectedFirmwareFile = null;
        this.upgradeError = this.$t("cyberboardR6.firmwareInvalidFile");
        return;
      }
      this.checkingFirmware = true;
      try {
        const packageInfo = await validateCyberboardR6FirmwarePackage(file);
        if (packageInfo.imageType !== "application") {
          throw new Error(this.$t("cyberboardR6.firmwareApplicationOnly"));
        }
        this.selectedFirmwareFile = file;
        this.upgradeError = "";
      } catch (error) {
        this.selectedFirmwareFile = null;
        this.upgradeError = `${this.$t("cyberboardR6.firmwareInvalidFile")}: ${
          error.message
        }`;
      } finally {
        this.checkingFirmware = false;
      }
    },
    setDeviceInfo(info) {
      this.productId = String((info && info.productId) || "").trim();
      this.version = String((info && info.productInfo) || "").trim();
    },
    stepState(index) {
      if (index < this.upgradeStep) return "done";
      if (index === this.upgradeStep)
        return this.upgradeError ? "error" : "active";
      return "pending";
    },
    openUpgradeWizard() {
      if (
        this.canResumeUpgrade ||
        (!this.localFirmwareMode && this.upgradeStep === 1)
      ) {
        this.upgradeModalVisible = true;
        return;
      }
      this.upgradeModalVisible = true;
      this.upgradeError = "";
      this.upgradeStep = 0;
      this.localFirmwareMode = false;
      this.selectedFirmwareFile = null;
      this.reconnectPending = false;
      this.dfuPort = null;
      this.progress = 0;
      this.progressStarted = false;
      this.statusText = "";
    },
    closeUpgradeWizard() {
      if (this.updating || this.checkingFirmware) return;
      this.upgradeModalVisible = false;
      this.upgradeError = "";
    },
    closeProgress() {
      this.progressStarted = false;
      this.statusText = "";
      this.upgradeError = "";
      if (this.progress >= 100) {
        this.localFirmwareMode = false;
        this.selectedFirmwareFile = null;
        this.reconnectPending = false;
        this.upgradeStep = 0;
        return;
      }
      this.upgradeModalVisible = false;
    },
    async advanceUpgradeStep() {
      if (this.reconnectPending) return this.verifyReconnect();
      if (this.localFirmwareMode) {
        if (this.upgradeStep === 0) {
          this.upgradeStep = 1;
          this.upgradeError = "";
          return;
        }
        if (this.upgradeStep === 1) return this.enterUpgradeMode();
        if (this.upgradeStep === 2) return this.selectDfuPort();
        if (this.upgradeStep === 3) return this.startUpdate();
      }
      if (this.upgradeStep === 0) return this.enterUpgradeMode();
      if (this.upgradeStep === 1) return this.selectDfuPort();
      if (this.upgradeStep === 2) return this.startUpdate();
      this.closeUpgradeWizard();
    },
    async enterUpgradeMode() {
      this.updating = true;
      this.upgradeError = "";
      this.statusText = this.$t("cyberboardR6.firmwareStepEnter");
      try {
        const bridge = getCyberboardR6Bridge();
        if (!bridge.isConnected) {
          const existing = await bridge.connectExisting();
          if (!existing) await bridge.connect();
        }
        const info =
          bridge.deviceInfo && bridge.deviceInfo.productId
            ? bridge.deviceInfo
            : await bridge.identify();
        bridge.deviceInfo = { ...(bridge.deviceInfo || {}), ...info };
        this.setDeviceInfo(info);
        if (!isR6FirmwareProductAllowed(this.productId)) {
          throw new Error(this.$t("cyberboardR6.firmwareUnsupportedDevice"));
        }
        if (!this.localFirmwareMode) {
          await this.checkOnlineFirmware();
          if (!this.canUpdateFirmware) {
            throw new Error(this.$t("cyberboardR6.firmwareAlreadyLatest"));
          }
        }
        await bridge.enterUpgradeMode();
        this.upgradeStep = this.localFirmwareMode ? 2 : 1;
        this.statusText = "";
      } catch (error) {
        if (error.message === this.$t("cyberboardR6.firmwareAlreadyLatest")) {
          this.upgradeError = "";
          this.statusText = error.message;
          this.upgradeModalVisible = false;
        } else {
          this.setUpgradeError(error);
        }
      } finally {
        this.updating = false;
      }
    },
    async selectDfuPort() {
      this.updating = true;
      this.upgradeError = "";
      this.statusText = this.$t("cyberboardR6.firmwareStepDfu");
      try {
        this.dfuPort = await requestCyberboardR6DfuPort();
        this.upgradeStep = this.localFirmwareMode ? 3 : 2;
        this.statusText = "";
      } catch (error) {
        if (error.name !== "NotFoundError") {
          this.setUpgradeError(error);
        } else {
          this.statusText = "";
          this.upgradeError = "";
        }
      } finally {
        this.updating = false;
      }
    },
    async startUpdate() {
      this.updating = true;
      this.progressStarted = true;
      this.upgradeError = "";
      this.progress = 0;
      this.statusText = this.$t("cyberboardR6.firmwareStepTransfer");
      this.upgradeStep = this.localFirmwareMode ? 3 : 2;
      this.reconnectPending = false;
      this.upgradeModalVisible = false;
      await this.$nextTick();
      try {
        const firmwareFile = this.localFirmwareMode
          ? this.selectedFirmwareFile
          : await this.downloadOnlineFirmware();
        if (!firmwareFile) {
          throw new Error(this.$t("cyberboardR6.firmwareInvalidFile"));
        }
        this.progress = 5;
        await updateCyberboardR6Firmware(firmwareFile, {
          port: this.dfuPort,
          onProgress: ({ progress }) => {
            this.progress = Math.min(
              90,
              10 + Math.floor((progress || 0) * 0.8)
            );
          },
          onLog: (event) => this.$emit("firmware-log", event),
        });
        this.reconnectPending = true;
        await this.verifyReconnect();
      } catch (error) {
        this.dfuPort = null;
        this.upgradeStep = this.localFirmwareMode ? 2 : 1;
        this.setUpgradeError(error);
      } finally {
        this.updating = false;
      }
    },
    async verifyReconnect() {
      this.updating = true;
      this.progressStarted = true;
      this.upgradeError = "";
      this.progress = 90;
      this.statusText = this.$t("cyberboardR6.firmwareStepReconnect");
      this.upgradeModalVisible = false;
      await this.$nextTick();
      try {
        const bridge = getCyberboardR6Bridge();
        const { info } = await waitForCyberboardR6Reconnect(bridge);
        if (!isR6FirmwareProductAllowed(info.productId)) {
          throw new Error(this.$t("cyberboardR6.firmwareUnsupportedDevice"));
        }
        this.setDeviceInfo(info);
        if (!this.localFirmwareMode) {
          const versionComparison = compareR6FirmwareVersions(
            this.version,
            this.onlineFirmwareMeta.version
          );
          if (versionComparison === null || versionComparison < 0) {
            throw new Error(
              this.$t("cyberboardR6.firmwareVersionVerificationFailed")
            );
          }
        }
        this.reconnectPending = false;
        this.progress = 100;
        this.statusText = this.$t("cyberboardR6.firmwareUpgradeVerified");
        this.upgradeStep = this.localFirmwareMode ? 4 : 3;
      } catch (error) {
        this.setUpgradeError(error);
      } finally {
        this.updating = false;
      }
    },
    async checkOnlineFirmware() {
      this.checkingFirmware = true;
      try {
        const response = await fetch(FIRMWARE_CHECK_API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            key: FIRMWARE_CHECK_KEY,
            password: FIRMWARE_CHECK_PASSWORD,
          }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        this.onlineFirmwareMeta = normalizeR6FirmwareResponse(payload);
      } finally {
        this.checkingFirmware = false;
      }
    },
    async downloadOnlineFirmware() {
      if (!this.canUpdateFirmware || !this.onlineFirmwareMeta) {
        throw new Error(this.$t("cyberboardR6.firmwareAlreadyLatest"));
      }
      const response = await fetch(this.onlineFirmwareMeta.url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      if (!blob.size) throw new Error("下载的固件包为空");
      return new File([blob], this.onlineFirmwareMeta.fileName, {
        type: "application/zip",
      });
    },
    setUpgradeError(error) {
      this.upgradeError = `${this.$t("loader.failToUpdate")}: ${error.message}`;
      this.statusText = this.upgradeError;
    },
  },
};
</script>

<style lang="scss" scoped>
.firmware-r6 {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 150px;
  color: #ffffff;
  font-size: 16px;

  .back {
    position: fixed;
    top: 24px;
    right: 24px;
    display: grid;
    width: 64px;
    height: 64px;
    place-items: center;
    color: #ffffff;
    cursor: pointer;

    &:hover {
      color: #b8b8b8;
    }

    &:focus-visible {
      outline: 2px solid #ffffff;
      outline-offset: 3px;
    }

    .iconfont {
      font-size: 56px;
    }
  }

  .logo {
    width: 300px;
    height: auto;
    margin-bottom: 60px;
  }

  .version {
    @include flex-center;
    justify-content: space-between;
    width: 720px;
    padding: 10px 0;
    margin-bottom: 30px;
    border-bottom: 3px solid #1d1d1d;
    font-size: 36px;

    span {
      letter-spacing: 0;
    }

    .code {
      color: #3c3c3c;
      font-size: 28px;
    }

    .iconfont {
      font-size: 28px;
    }
  }

  .description {
    width: 720px;
    height: 250px;
    margin-bottom: 30px;
  }

  .button {
    @include linear-button;
    width: 480px;
    height: 80px;
    margin-bottom: 26px;
    line-height: 80px;

    &:disabled {
      cursor: wait;
      opacity: 0.5;
    }
  }

  .firmware-status {
    min-height: 28px;
    margin-bottom: 12px;
    color: #b8b8b8;
    font-size: 20px;
    text-align: center;
  }

  .tip {
    height: 28px;
    line-height: 28px;
    font-size: 24px;
    text-align: center;
  }
}
</style>

<style lang="scss">
.ant-modal-wrap.r6-firmware-modal.px .ant-modal {
  width: 1040px !important;
  max-width: calc(100vw - 40px);
}

.ant-modal-wrap.r6-firmware-modal.px .ant-modal-content,
.ant-modal-wrap.r6-firmware-modal.px .ant-modal-header {
  border-radius: 8px;
  background: #202020;
}

.ant-modal-wrap.r6-firmware-modal.px .ant-modal-header {
  border-bottom: 1px solid #363636;
}

.ant-modal-wrap.r6-firmware-modal.px .ant-modal-title {
  color: #ffffff;
  font-size: 22px;
}

.ant-modal-wrap.r6-firmware-modal.px .ant-modal-close {
  color: #ffffff;
}

.ant-modal-wrap.r6-firmware-modal.px .ant-modal-body {
  padding: 30px 38px 32px;
  color: #ffffff;
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-steps {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 0 0 28px;
  padding: 0;
  list-style: none;
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-step {
  position: relative;
  display: flex;
  min-width: 0;
  align-items: center;
  flex-direction: column;
  gap: 10px;
  padding: 0 10px;
  color: #848484;
  text-align: center;
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-step:not(:last-child)::after {
  position: absolute;
  top: 13px;
  left: calc(50% + 17px);
  width: calc(100% - 34px);
  height: 1px;
  background: #454545;
  content: "";
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-step-marker {
  position: relative;
  z-index: 1;
  display: grid;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  place-items: center;
  border: 1px solid #555555;
  border-radius: 50%;
  background: #202020;
  color: #9e9e9e;
  font-size: 15px;
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-step > strong {
  min-height: 38px;
  max-width: 100%;
  color: inherit;
  font-size: 17px;
  font-weight: 500;
  line-height: 1.35;
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-step.active .wizard-step-marker {
  border-color: #8743ff;
  box-shadow: 0 0 0 4px #8743ff22;
  color: #ffffff;
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-step.active {
  color: #ffffff;
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-step.done {
  color: #d5f4e2;
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-step.done .wizard-step-marker {
  border-color: #55c68a;
  background: #55c68a;
  color: #111111;
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-step.error .wizard-step-marker {
  border-color: #e36b6b;
  color: #e36b6b;
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-error {
  margin: 20px 0 0;
  padding: 13px 16px;
  border-left: 2px solid #e36b6b;
  background: #3a2525;
  color: #ffb0b0;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-actions {
  display: flex;
  justify-content: center;
  padding-top: 28px;
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-next {
  width: 220px;
  height: 48px;
  box-sizing: border-box;
  padding: 0 24px;
  border: 1px solid #8743ff;
  border-radius: 4px;
  background: #8743ff;
  color: #ffffff;
  cursor: pointer;
  font-size: 16px;
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-next:hover:not(:disabled) {
  border-color: #9a62ff;
  background: #9a62ff;
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-panel {
  display: flex;
  min-height: 142px;
  flex-direction: column;
  justify-content: center;
  gap: 12px;
  color: #ffffff;
  text-align: center;
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-panel > strong {
  font-size: 19px;
  font-weight: 600;
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-panel > span {
  color: #a8a8a8;
  font-size: 17px;
  line-height: 1.5;
}

.ant-modal-wrap.r6-firmware-modal.px .firmware-version-row {
  display: flex;
  align-items: baseline;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px 14px;
  color: #a8a8a8;
  font-size: 16px;
}

.ant-modal-wrap.r6-firmware-modal.px .firmware-version-row code {
  color: #ffffff;
  font-family: inherit;
  font-size: 17px;
  overflow-wrap: anywhere;
}

.ant-modal-wrap.r6-firmware-modal.px .firmware-version-row.target code {
  color: #d5f4e2;
}

.ant-modal-wrap.r6-firmware-modal.px .firmware-version-arrow {
  color: #8743ff;
  font-size: 22px;
  line-height: 1;
}

.ant-modal-wrap.r6-firmware-modal.px .firmware-file-picker {
  position: relative;
  display: flex;
  width: 220px;
  height: 48px;
  box-sizing: border-box;
  align-self: center;
  align-items: center;
  justify-content: center;
  padding: 8px 24px;
  border: 1px solid #5b8588;
  border-radius: 4px;
  background: #34494b;
  color: #ffffff;
  cursor: pointer;
  font-size: 16px;
  line-height: 1.35;
  overflow-wrap: anywhere;
  text-align: center;
}

.ant-modal-wrap.r6-firmware-modal.px .firmware-file-picker:hover {
  border-color: #78a7a8;
  background: #3d5759;
}

.ant-modal-wrap.r6-firmware-modal.px .firmware-file-picker.selected {
  width: auto;
  max-width: 100%;
  height: auto;
  min-height: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #b9d0d0;
  text-decoration: underline;
  text-underline-offset: 4px;
}

.ant-modal-wrap.r6-firmware-modal.px .firmware-file-picker.selected:hover {
  background: transparent;
  color: #ffffff;
}

.ant-modal-wrap.r6-firmware-modal.px .firmware-file-picker.disabled {
  cursor: wait;
  opacity: 0.55;
}

.ant-modal-wrap.r6-firmware-modal.px .firmware-file-picker input {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  clip-path: inset(50%);
}

.ant-modal-wrap.r6-firmware-modal.px .firmware-file-picker:focus-within {
  outline: 2px solid #78a7a8;
  outline-offset: 2px;
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-changelog {
  display: flex;
  max-height: 76px;
  flex-direction: column;
  gap: 4px;
  margin: 2px 0 0;
  overflow: auto;
  color: #a8a8a8;
  font-size: 14px;
  line-height: 1.45;
  text-align: left;
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-changelog strong {
  color: #ffffff;
}

.ant-modal-wrap.r6-firmware-modal.px .wizard-next:disabled {
  cursor: wait;
  opacity: 0.45;
}

@media (max-width: 620px) {
  .ant-modal-wrap.r6-firmware-modal.px .ant-modal {
    max-width: calc(100vw - 20px);
  }

  .ant-modal-wrap.r6-firmware-modal.px .ant-modal-body {
    padding: 22px 16px 24px;
  }

  .ant-modal-wrap.r6-firmware-modal.px .wizard-steps {
    margin-bottom: 22px;
  }

  .ant-modal-wrap.r6-firmware-modal.px .wizard-step {
    padding: 0 3px;
  }

  .ant-modal-wrap.r6-firmware-modal.px .wizard-step > strong {
    font-size: 13px;
  }

  .ant-modal-wrap.r6-firmware-modal.px .wizard-panel {
    min-height: 150px;
  }
}
</style>
